import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Users, Calendar, Trophy, Sparkles, ArrowRight, HandCoins, Info, XCircle } from "lucide-react";
import api from "../lib/api";

const PURPOSES = [
  { key: "small_business", label: "Start a small business" },
  { key: "expand_business", label: "Expand an existing business" },
  { key: "farming", label: "Farming / Agriculture" },
  { key: "skills", label: "Skills / Tools / Equipment" },
  { key: "education", label: "Education or vocational training" },
  { key: "women", label: "Women's empowerment" },
  { key: "youth", label: "Youth empowerment" },
  { key: "community", label: "Community livelihood activity" },
  { key: "other", label: "Other" },
];

const AWARDS = [
  { amount: "₦1,000,000", title: "First empowerment beneficiary", perks: "Branded T-shirt, cap, certificate & follow-up support." },
  { amount: "₦200,000", title: "Second empowerment beneficiary", perks: "Branded T-shirt and cap." },
  { amount: "₦200,000", title: "Third empowerment beneficiary", perks: "Branded T-shirt and cap." },
];

function StatBox({ value, label, testid }) {
  return (
    <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur" data-testid={testid}>
      <div className="font-serif text-3xl md:text-4xl font-bold text-[#d6aa43]">{value}</div>
      <div className="mt-2 text-xs uppercase tracking-[0.18em] text-white">{label}</div>
    </div>
  );
}

export default function Empowerment() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    document.title = "Empowerment Programme | Dutsen Kauna Foundation";
    api.get("/programme-stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const fmt = (n) => (n === undefined || n === null ? "—" : Number(n).toLocaleString());

  return (
    <div data-testid="empowerment-page">
      {/* Hero */}
      <section className="relative bg-[#155e3f] text-white py-20 md:py-28 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1509099381441-ea3c0cf98b94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwd29tZW4lMjBhZ3JpY3VsdHVyZXxlbnwwfHx8fDE3ODI2MDk1NTN8MA&ixlib=rb-4.1.0&q=85" alt="A woman running a small tailoring business supported by the empowerment programme" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] via-[#155e3f]/85 to-[#155e3f]/40" />
        <div className="dk-container relative">
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Empowerment & Community Support Programme</span>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold mt-4 max-w-4xl leading-[1.05]">
            Giving hope. Creating opportunity. <span className="italic text-[#d6aa43]">Transforming lives.</span>
          </h1>
          <p className="mt-6 text-white text-lg max-w-3xl">
            Register completely free of charge for an opportunity to receive empowerment support.
            Donating is <strong className="text-white">never</strong> required and does <strong className="text-white">not</strong> increase your chance of selection.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/empowerment/register" className="dk-btn-primary" data-testid="empowerment-register-hero">
              Register free as a beneficiary <ArrowRight size={16} />
            </Link>
            <Link to="/donate" className="dk-btn-outline" data-testid="empowerment-donate-hero">
              Support the programme
            </Link>
          </div>
        </div>
      </section>

      {/* Live Dashboard */}
      <section className="py-20 bg-[#155e3f] text-white" data-testid="programme-dashboard">
        <div className="dk-container">
          <div className="mb-10 max-w-2xl">
            <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Live programme dashboard</span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3">Real numbers. Public accountability.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <StatBox testid="dash-registered" value={fmt(stats?.registered_beneficiaries)} label="Registered beneficiaries" />
            <StatBox testid="dash-verified" value={fmt(stats?.verified_beneficiaries)} label="Verified beneficiaries" />
            <StatBox testid="dash-empowered" value={fmt(stats?.people_empowered)} label="People empowered" />
            <StatBox testid="dash-empowerment-total" value={`₦${fmt(stats?.total_empowerment_amount_ngn)}`} label="Total empowerment provided" />
            <StatBox testid="dash-families" value={fmt(stats?.families_supported)} label="Families supported" />
            <StatBox testid="dash-food" value={`₦${fmt(stats?.food_support_amount_ngn)}`} label="Food support provided" />
            <StatBox testid="dash-donations" value={`₦${fmt(stats?.total_donations_ngn)}`} label="Total donations (NGN)" />
            <StatBox testid="dash-next" value={stats?.next_empowerment_selection || "TBA"} label="Next empowerment selection" />
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/empowerment/register" className="dk-btn-primary" data-testid="dash-register">Register free</Link>
            <Link to="/donate" className="dk-btn-outline">Donate</Link>
            <Link to="/impact" className="dk-btn-outline">View impact</Link>
          </div>
        </div>
      </section>

      {/* Monthly Awards */}
      <section className="py-20 bg-[#f8f4e8]">
        <div className="dk-container">
          <div className="max-w-3xl mb-12">
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Monthly empowerment awards</span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0a0d12] font-bold mt-3">
              Three people supported every month — <span className="italic text-[#d6aa43]">₦1,400,000</span> total.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {AWARDS.map((a, i) => (
              <div key={a.title} className={`p-8 rounded-2xl ${i === 0 ? "bg-[#155e3f] text-white ring-2 ring-[#d6aa43]" : "bg-white border border-black/5"}`} data-testid={`award-${i}`}>
                <Trophy className={i === 0 ? "text-[#d6aa43]" : "text-[#0d1724]"} />
                <div className={`font-serif text-4xl md:text-5xl font-bold mt-4 ${i === 0 ? "text-[#d6aa43]" : "text-[#0a0d12]"}`}>{a.amount}</div>
                <h3 className={`font-serif text-xl mt-2 font-bold ${i === 0 ? "text-white" : "text-[#0a0d12]"}`}>{a.title}</h3>
                <p className={`mt-3 text-sm leading-relaxed ${i === 0 ? "text-white" : "text-[#0a0d12]/70"}`}>{a.perks}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-white">
        <div className="dk-container">
          <div className="max-w-3xl mb-12">
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">How the selection works</span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0a0d12] font-bold mt-3">Transparent. Two-stage. Public.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Stage 1 — Basic eligibility", text: "Foundation verifies the registration is genuine, applicant is identifiable, information is accurate, no duplicates, and not disqualified under programme rules.", icon: ShieldCheck },
              { title: "Stage 2 — Random selection", text: "Every eligible beneficiary serial enters the monthly pool once. The system randomly draws the ₦1m recipient, then two ₦200k recipients. Each serial can be drawn only once per month.", icon: Sparkles },
              { title: "Public selection", text: "Selection can be conducted live on the website, Facebook Live, YouTube Live or at a Foundation event before an independent committee. Only serial numbers are shown publicly — never private information.", icon: Users },
              { title: "Reserve beneficiaries", text: "Reserve numbers are generated to replace primary selected beneficiaries who cannot be verified, cannot be contacted, or violate programme rules.", icon: HandCoins },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="p-7 rounded-2xl bg-[#f8f4e8] border border-[#d6aa43]/20" data-testid={`how-${i}`}>
                  <div className="w-11 h-11 rounded-full bg-[#155e3f] text-[#d6aa43] flex items-center justify-center mb-4"><Icon size={20} /></div>
                  <h3 className="font-serif text-xl text-[#0a0d12] font-bold mb-2">{s.title}</h3>
                  <p className="text-[#0a0d12]/75 leading-relaxed">{s.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Equal opportunity notice */}
      <section className="py-16 bg-[#155e3f] text-white">
        <div className="dk-container max-w-4xl">
          <div className="p-8 md:p-10 rounded-2xl bg-white/[0.05] border-l-4 border-[#d6aa43]">
            <div className="flex items-center gap-2 mb-3">
              <Info className="text-[#d6aa43]" size={20} />
              <span className="text-xs uppercase tracking-[0.25em] text-[#d6aa43] font-semibold">Everyone has an equal opportunity</span>
            </div>
            <h3 className="font-serif text-2xl md:text-3xl font-bold mb-4">
              You do not need to donate to participate. One person = one beneficiary number.
            </h3>
            <ul className="space-y-2 text-white">
              {[
                "Non-donor + free registration = 1 beneficiary entry",
                "Donor + no beneficiary registration = 0 beneficiary entries",
                "Donor + free beneficiary registration = 1 beneficiary entry",
                "Large donor + free beneficiary registration = 1 beneficiary entry",
                "Recurring donor + free beneficiary registration = 1 beneficiary entry",
              ].map((t) => (
                <li key={t} className="flex gap-2"><CheckCircle2 size={16} className="text-[#d6aa43] mt-0.5 flex-shrink-0" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="mt-8 grid md:grid-cols-2 gap-4 text-sm">
            <div className="p-5 rounded-xl bg-white/[0.03] flex gap-3">
              <XCircle className="text-red-400 flex-shrink-0" size={18} />
              <div><strong className="text-white">Never pay</strong> registration fees, processing fees, selection fees, verification fees or award-collection fees. Registration is 100% free.</div>
            </div>
            <div className="p-5 rounded-xl bg-white/[0.03] flex gap-3">
              <CheckCircle2 className="text-[#d6aa43] flex-shrink-0" size={18} />
              <div>Donation history <strong className="text-white">carries 0 points</strong> in the needs-based Family Food Support decision. It does not influence outcomes.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Family Food Support */}
      <section className="py-20 bg-[#f8f4e8]" data-testid="family-food-section">
        <div className="dk-container grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Second programme</span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0a0d12] font-bold mt-3 mb-5">Family Food Support</h2>
            <p className="text-[#0a0d12]/75 leading-relaxed mb-4">
              Every three months, two families each receive <strong>₦200,000</strong> in food assistance — total <strong>₦400,000 per quarter</strong>.
              Selection is <strong>needs-based</strong>, not random.
            </p>
            <ul className="space-y-2 text-sm text-[#0a0d12]/80 mb-6">
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#d6aa43] mt-0.5" /> Financial hardship — 30 points</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#d6aa43] mt-0.5" /> Household dependants — 20 points</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#d6aa43] mt-0.5" /> Vulnerability — 20 points</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#d6aa43] mt-0.5" /> Food insecurity — 20 points</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#d6aa43] mt-0.5" /> Verification — 10 points</li>
            </ul>
            <Link to="/family-support" className="dk-btn-primary" data-testid="family-apply-cta">
              Apply for family food support <ArrowRight size={16} />
            </Link>
          </div>
          <div className="p-8 rounded-2xl bg-white border border-black/5">
            <Calendar className="text-[#d6aa43] mb-4" />
            <h3 className="font-serif text-2xl text-[#0a0d12] font-bold mb-3">₦400,000 / quarter</h3>
            <p className="text-[#0a0d12]/70 leading-relaxed">
              Families apply free of charge. Each application receives a unique reference like
              <strong className="text-[#0a0d12]"> DKF-FOOD-2026-000234</strong>. Applications are ranked by the published
              criteria, reviewed by the assessment committee, and two families are approved every quarter.
            </p>
            <p className="text-xs uppercase tracking-[0.2em] text-[#0d1724]/60 font-semibold mt-6">
              Donation history carries 0 points.
            </p>
          </div>
        </div>
      </section>

      {/* Purpose gallery */}
      <section className="py-20 bg-white">
        <div className="dk-container">
          <div className="max-w-2xl mb-8">
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">What can support fund?</span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0a0d12] font-bold mt-3">Choose your empowerment path.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3">
            {PURPOSES.map((p) => (
              <div key={p.key} className="p-5 rounded-xl border border-black/5 bg-[#f8f4e8] text-[#0a0d12] font-semibold text-sm">
                {p.label}
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/empowerment/register" className="dk-btn-primary" data-testid="empowerment-register-bottom">
              Register free as a beneficiary <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
